import React, { useState } from 'react';
import { Button, Modal, Table, Spin, message, Alert, Collapse, Tag } from 'antd';
import { BugFilled } from '@ant-design/icons';
import { httpClient } from "../services/http.service";
import { useNavigate } from "react-router-dom";

export function RepairDiagnostic({ rfqId }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const navigate = useNavigate();

  const loadDiagnostic = () => {
    setIsLoading(true);
    httpClient
      .get(`/api/v1/RFQ/${rfqId}/repair/diagnostic`)
      .then((response) => {
        setDiagnosticData(response?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || err?.message || 'Failed to load diagnostic data');
        setIsLoading(false);
      });
  };

  const handleClick = () => {
    loadDiagnostic();
    setIsModalVisible(true);
  };

  return (
    <>
      <Button 
        style={{ margin: "5px" }} 
        danger
        size='sm' 
        onClick={handleClick}
      > 
        <BugFilled /> Repair Diagnostic
      </Button>

      <Modal
        title="Repair Data Diagnostic"
        visible={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        width={900}
      >
        <Spin spinning={isLoading}>
          {diagnosticData && (
            <>
              {(diagnosticData?.discrepancySCSR > 0 || diagnosticData?.discrepancyItems > 0) && (
                <Alert
                  message="Data Discrepancy Detected"
                  description={`
                    ${diagnosticData?.discrepancySCSR > 0 ? `${diagnosticData?.discrepancySCSR} SCSRs have no repair records. ` : ''}
                    ${diagnosticData?.discrepancyItems > 0 ? `${diagnosticData?.discrepancyItems} repairs have missing items.` : ''}
                  `}
                  type="warning"
                  showIcon
                  style={{ marginBottom: '15px' }}
                />
              )}

              <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                <p><strong>Current RFQ:</strong> {diagnosticData?.currentRFQNumber} (ID: {diagnosticData?.currentRFQId})</p>
                <p><strong>Total SCSRs in RFQ:</strong> {diagnosticData?.totalSCSRsInRFQ}</p>
                <p><strong>Repair Records Linked to This RFQ:</strong> {diagnosticData?.totalRepairRecords}</p>
                <p><strong>Total Items Repaired:</strong> {diagnosticData?.totalItemsRepaired}</p>
                <hr />
                <p style={{color: 'red', fontWeight: 'bold'}}>
                  SCSRs WITHOUT Repairs: {diagnosticData?.scsrsWithoutRepairsCount}
                </p>
              </div>

              {diagnosticData?.repairsInOtherRFQs?.length > 0 && (
                <>
                  <Alert
                    message={`✅ Found ${diagnosticData?.repairsInOtherRFQs?.length} repairs for the missing SCSRs!`}
                    description={`These repairs are linked to OTHER RFQs. Click the RFQ link to view them.`}
                    type="success"
                    showIcon
                    style={{ marginBottom: '15px' }}
                  />
                  <h4>Missing SCSRs - But Repairs Exist in Other RFQs:</h4>
                  <Table
                    dataSource={diagnosticData?.repairsInOtherRFQs}
                    size="small"
                    pagination={false}
                    rowKey="repairId"
                    expandable={{
                      expandedRowRender: (record) => (
                        <div style={{ marginLeft: '20px' }}>
                          <h5>Items Repaired:</h5>
                          {record?.itemsRepaired?.length > 0 ? (
                            <div>
                              {record?.itemsRepaired?.map((item, idx) => (
                                <div key={idx} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                                  <Tag color="blue">{item?.description}</Tag>
                                  <p style={{ marginBottom: '0' }}>
                                    <strong>Details:</strong> {item?.details}
                                  </p>
                                  <p style={{ marginBottom: '0' }}>
                                    <strong>Cost:</strong> R {item?.cost}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p>No items recorded</p>
                          )}
                        </div>
                      ),
                    }}
                    columns={[
                      {
                        title: 'SCSR Serial',
                        dataIndex: 'scsrSerialNumber',
                        key: 'scsrSerialNumber',
                        render: (text) => <span style={{fontWeight: 'bold', color: 'green'}}>{text}</span>
                      },
                      {
                        title: 'Repair Status',
                        dataIndex: 'repairStatus',
                        key: 'repairStatus',
                      },
                      {
                        title: 'Items',
                        dataIndex: 'itemsCount',
                        key: 'itemsCount',
                      },
                      {
                        title: 'Currently Linked RFQ',
                        dataIndex: 'linkedRFQNumber',
                        key: 'linkedRFQNumber',
                        render: (text, record) => (
                          <Button
                            type="link"
                            onClick={() => navigate(`/app/repairs/${record.linkedRFQId}`)}
                            style={{ padding: 0 }}
                          >
                            <div style={{color: 'orange', fontWeight: 'bold', textDecoration: 'underline'}}>
                              {text}
                            </div>
                            <small>{record.linkedRFQStatus}</small>
                          </Button>
                        )
                      },
                      {
                        title: 'Creation Date',
                        dataIndex: 'creationDate',
                        key: 'creationDate',
                        render: (date) => new Date(date).toLocaleString(),
                      },
                    ]}
                  />
                  <br />
                </>
              )}

              {diagnosticData?.scsrsWithoutRepairs?.length > 0 && diagnosticData?.repairsInOtherRFQs?.length === 0 && (
                <>
                  <Alert
                    message={`⚠️ ${diagnosticData?.scsrsWithoutRepairs?.length} SCSRs have NO repair records`}
                    description="These SCSRs were never repaired. Create repair records for them."
                    type="error"
                    showIcon
                    style={{ marginBottom: '15px' }}
                  />
                  <h4>SCSRs Missing Repairs:</h4>
                  <Table
                    dataSource={diagnosticData?.scsrsWithoutRepairs}
                    size="small"
                    pagination={false}
                    rowKey="scsrId"
                    columns={[
                      {
                        title: 'SCSR Serial',
                        dataIndex: 'serialNumber',
                        key: 'serialNumber',
                        render: (text) => <span style={{fontWeight: 'bold', color: 'red'}}>{text}</span>
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                      },
                    ]}
                  />
                  <br />
                </>
              )}

              {diagnosticData?.repairsWithMissingItems?.length > 0 && (
                <>
                  <Alert
                    message={`⚠️ Found ${diagnosticData?.repairsWithMissingItems?.length} repairs with ZERO items`}
                    description="These repairs have no items in their itemsRepaired array. Edit them to add missing items."
                    type="error"
                    showIcon
                    style={{ marginBottom: '15px' }}
                  />
                  <h4>Repairs Missing Items (Click to edit):</h4>
                  <Table
                    dataSource={diagnosticData?.repairsWithMissingItems}
                    size="small"
                    pagination={false}
                    rowKey="_id"
                    columns={[
                      {
                        title: 'SCSR Serial',
                        dataIndex: ['scsr'],
                        key: 'scsr',
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                      },
                      {
                        title: 'Items Count',
                        dataIndex: 'itemsCount',
                        key: 'itemsCount',
                        render: (count) => (
                          <span style={{ color: count === 0 ? 'red' : 'green' }}>
                            {count}
                          </span>
                        ),
                      },
                      {
                        title: 'Creation Date',
                        dataIndex: 'creationDate',
                        key: 'creationDate',
                        render: (date) => new Date(date).toLocaleString(),
                      },
                    ]}
                  />
                </>
              )}
            </>
          )}
        </Spin>
      </Modal>
    </>
  );
}
